import React, { ReactNode, useCallback, useState } from "react";

import { v4 as uuidv4 } from "uuid";

import ModalsContext, { ModalsContextValue } from "./ModalsContext";
import ModalState from "./ModalState";

const MINIMIZED_MODAL_WIDTH = 300;
const MINIMIZED_MODAL_HEIGHT = 60;

const COLLAPSED_MODAL_WIDTH = 500;
const COLLAPSED_MODAL_HEIGHT = 600;

const MODAL_MARGIN = 50;

const ModalsProvider = ({ children }: { children: ReactNode }) => {
  const [modals, setModals] = useState<ModalState[]>([]);

  const updateModal = useCallback((id: ModalState["id"], data: Partial<Omit<ModalState, "id">>) => {
    setModals((prevModals) =>
      prevModals.map((modal) => (modal.id === id ? { ...modal, ...data } : modal))
    );
  }, []);

  const initialize = useCallback<ModalsContextValue["initialize"]>(() => {
    const id = uuidv4();
    setModals((prevModals) => [...prevModals, { id, status: "normal" }]);
    return id;
  }, []);

  const destroy = useCallback<ModalsContextValue["destroy"]>((id) => {
    setModals((prevModals) => prevModals.filter((modal) => modal.id !== id));
  }, []);

  const minimize = useCallback<ModalsContextValue["minimize"]>(
    (id) => {
      updateModal(id, {
        bottom: MODAL_MARGIN,
        height: MINIMIZED_MODAL_HEIGHT,
        right: MODAL_MARGIN,
        status: "minimized",
        width: MINIMIZED_MODAL_WIDTH,
      });
    },
    [updateModal]
  );

  const restore = useCallback<ModalsContextValue["restore"]>(
    (id) => {
      updateModal(id, {
        bottom: undefined,
        height: undefined,
        right: undefined,
        status: "normal",
        width: undefined,
      });
    },
    [updateModal]
  );

  const collapse = useCallback<ModalsContextValue["collapse"]>(
    (id) => {
      updateModal(id, {
        bottom: MODAL_MARGIN,
        height: COLLAPSED_MODAL_HEIGHT,
        right: MODAL_MARGIN,
        status: "collapsed",
        width: COLLAPSED_MODAL_WIDTH,
      });
    },
    [updateModal]
  );

  return (
    <ModalsContext.Provider value={{ collapse, destroy, initialize, minimize, modals, restore }}>
      {children}
    </ModalsContext.Provider>
  );
};

export default ModalsProvider;
